/**
 * Which of the two products this browser tab is: Husly's self-service, or the
 * operator's client portal.
 *
 * One build and one container (port 3314) serve both. `account.husly.app` is
 * Husly's own hub, where plans, the credit wallet, usage meters, API keys and
 * the referral programme all belong. `portal.webadmin.ee` is where the
 * operator's clients arrive from an invite to read their invoices, projects,
 * tickets and contracts - and every one of those Husly features is somebody
 * else's product to them. Showing a client an "upgrade your plan" tab under a
 * logo they have never bought anything from is not a cosmetic problem.
 *
 * Why the host and not a build-time flag: `web_account` reads no `import.meta.env`
 * at all, which is exactly what lets one image answer on both hosts (see the
 * portal.webadmin.ee block in deployment/husly/nginx-husly.conf). Introducing a
 * build-time brand would mean two builds, two containers and two deploy paths
 * for one app. The host is the honest signal here: it is what nginx routed on,
 * what the cookie domain was derived from, and what the client sees in the
 * address bar.
 *
 * `crossAppUrls` in lib/auth.ts reads the same host for the same reason.
 */
import type { SiteTheme } from "@wa/core/theme";

/**
 * Hosts that serve the client portal. The rule is the FIRST label, so a second
 * operator domain needs one entry and no other change.
 *
 * Kept as a list rather than a bare `startsWith("portal.")` so that adding
 * `klient.` or a per-operator host later is an edit in one obvious place, and so
 * that a tenant site called portal.<something>.husly.app can never accidentally
 * claim the portal brand - the base domain has to match too.
 */
const PORTAL_HOSTS = ["portal.webadmin.ee"];

/** True when this tab is the operator's client portal rather than Husly's hub. */
export function isClientPortalHost(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  if (PORTAL_HOSTS.includes(host)) return true;
  // Local development: there is only one host, so the brand is opt-in per tab.
  // `?brand=portal` survives in the URL, which is enough to look at the shell;
  // it is deliberately NOT persisted, because a sticky flag in localStorage
  // would follow the developer back to the Husly views and confuse the next bug.
  if (host === "localhost" || host.endsWith(".localhost")) {
    return new URLSearchParams(window.location.search).get("brand") === "portal";
  }
  return false;
}

/**
 * The wordmark shown in the header, per brand.
 *
 * The portal's `?v=` is a cache key, like `PORTAL_FAVICON`'s: nginx serves public/ as
 * immutable, so bump it whenever the file's content changes (v2 = the 2026-10 logo).
 */
export function brandLogo(): { light: string; dark: string; alt: string } {
  return isClientPortalHost()
    ? { light: "/brand-portal.svg?v=2", dark: "/brand-portal-dark.svg?v=2", alt: "WebAdmin" }
    : { light: "/logo.svg?v=brown", dark: "/logo-dark.svg?v=brown", alt: "Husly" };
}

/**
 * The operator's own brand colours, the ones webadmin.ee itself is drawn in.
 *
 * The portal used to inherit the HOST theme, which is Husly's: a WebAdmin
 * wordmark over Husly's copper buttons. A client who arrives from a WebAdmin
 * invoice reads that as two companies on one page.
 *
 * One hue on purpose. webadmin.ee also carries a blue and a pink, but in a
 * portal the second colour has a job already - red is "overdue", green is
 * "paid" - and a decorative accent beside them would compete with the two
 * colours that mean something.
 *
 * `primary` is the brighter of the two violets because it is the one that
 * becomes link and badge TEXT, and it has to stay readable on a white card and
 * on a dark one alike (about 4.2:1 on both). The deeper `secondary` is only the
 * far end of the primary button's gradient, where white ink sits on it.
 */
export const PORTAL_PALETTE = {
  primary: "#8b5cf6",
  secondary: "#7c3aed",
  accent: "#8b5cf6",
} as const;

/**
 * The theme this tab should wear: the host theme as it came, or the same theme
 * in the operator's colours on the portal host.
 *
 * Everything that is NOT a brand colour passes through untouched - the corner
 * radius, the sign-in layout, the fonts - because those are the host's settings
 * and the portal has none of its own. The gradient is restated rather than
 * inherited: a host gradient in "custom" mode carries two colours of its own,
 * and they would put the host's copper back on every primary button.
 *
 * A null theme still gets the palette. The host theme is one fetch that can
 * fail, and a portal that falls back to the kit's default indigo on a slow
 * network is the same two-brand page by another route.
 */
export function brandTheme(theme: SiteTheme | null | undefined): SiteTheme | null {
  if (!isClientPortalHost()) return theme ?? null;
  return {
    ...(theme ?? {}),
    ...PORTAL_PALETTE,
    gradient: { enabled: true, mode: "brand", angle: 135 },
  };
}

/**
 * The operator's public pages a signed-in client may need: the two legal texts
 * and a way to reach a person.
 *
 * Addresses rather than catalogue strings, because they are not copy - a
 * translator must not be able to change where "Privacy policy" leads. Estonian
 * has its own slugs; every other language gets the English pages, the same
 * fallback the catalogues use.
 */
export function portalLinks(locale: string): { privacy: string; terms: string; contact: string } {
  const base = "https://webadmin.ee";
  return locale === "et"
    ? { privacy: `${base}/privaatsuspoliitika`, terms: `${base}/kasutustingimused`, contact: `${base}/kontakt` }
    : { privacy: `${base}/en/privacy-policy`, terms: `${base}/en/terms-of-use`, contact: `${base}/en/contact` };
}

/**
 * The portal's tab icon: the white WebAdmin w in a violet circle, so it stays
 * visible on a light and on a dark tab strip, like the CRM's icon.
 *
 * The query is a cache key, not a variant: nginx serves public/ as immutable
 * for a year, so a browser that already holds an older copy of the file would
 * keep showing it. Bump it whenever the file's content changes.
 */
export const PORTAL_FAVICON = "/brand-portal-icon.svg?v=4";

/**
 * Swap the tab icon on the portal host.
 *
 * The favicon is written into index.html, which is one static file for both
 * hosts, so it cannot be branded at build time the way the wordmark can. It is
 * also the piece of chrome that outlives the page: a bookmarked portal showing
 * Husly's mark is a client filing our supplier's brand under their own.
 *
 * Call once at boot, before React renders - the icon is chrome, not state, and
 * nothing re-renders it.
 */
export function applyBrandFavicon(): void {
  if (typeof document === "undefined" || !isClientPortalHost()) return;
  const href = PORTAL_FAVICON;
  const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (existing) {
    existing.href = href;
    return;
  }
  const link = document.createElement("link");
  link.rel = "icon";
  link.type = "image/svg+xml";
  link.href = href;
  document.head.appendChild(link);
}
